import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { ServicesSubSectionHeader } from '../../features/services/services-subsection/services-subsection.styles';
import {
  DeliveryOptionCardsContainer,
  ServicesSubSectionContainerForDelivery,
} from './parcel-delivery.styles';
import DeliveryOptionCard from '../../features/parcel-creation/parcel-delivery-option/delivery-option-card.components';
import { useStore } from '../../stores/store';
import {
  DeliveryMethod,
  DeliveryOptionSortType,
} from '../../models/delivery-options/common';
import MiddleMileFilter from './middle-mile-filter/middle-mile-filter';
import DeliveryOptionsSorting from './options-sorting/options-sorting.component';
import {
  assignDelivetyOptionLabels,
  filterDeliveryOptions,
  sortDeliveryOptions,
  sortOptions,
} from './helpers';
import { FlexContainer } from '../../common/flex-container/flex-container.styles';
import { IDropdownItem } from '../../common/select/select.component';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';

import {
  lockBodyScroll,
  unlockBodyScroll,
} from '../../utils/generic/generic.utils';
import HintsSidebar from './hints-sidebar/hints-sidebar.component';
import { useSwipe } from '../../hooks/use-swipe.hook';

const ParcelDelivery = () => {
  const {
    parcelCreationStore: {
      deliveryOptions,
      setDeliveryOption,
      selectedDeliveryOption,
      deliveryOptionHints,
      isNextButtonClicked,
      selectedMiddleMileFilter,
      setMiddleMileFilter,
      setIsNextButtonClicked,
      setSelectedFirstMile,
      setSelectedLastMile,
    },
    userStore: { getCurrencySymbol },
    navStore: { setSkipScrollUnlock },
    commonStore: { toastError },
  } = useStore();

  const { t } = useTranslation();

  const isMobile = useIsMobile();

  const [activeSort, setActiveSort] = useState<
    IDropdownItem<DeliveryOptionSortType>
  >(sortOptions[0]);

  const [
    shouldTriggerShrinkWidthAnimation,
    setShouldTriggerShrinkWidthAnimation,
  ] = useState(false);

  const handleMidleMileFilterChange = (filter: DeliveryMethod | undefined) => {
    setMiddleMileFilter(filter);
    if (filter === undefined) {
      setShouldTriggerShrinkWidthAnimation(true);
    } else {
      setShouldTriggerShrinkWidthAnimation(false);
    }
  };

  useSwipe(() => {
    const availableFilters = Array.from(
      new Set(deliveryOptions?.map((item) => item.middle_mile.delivery_method))
    );
    if (availableFilters.length < 2) return;
    handleMidleMileFilterChange(undefined);
    setDeliveryOption(undefined);
  });

  useEffect(() => {
    if (!isMobile) return;

    lockBodyScroll();
    setSkipScrollUnlock(true);

    return () => {
      unlockBodyScroll();
      setSkipScrollUnlock(false);
    };
  }, [isMobile, setSkipScrollUnlock]);

  useEffect(() => {
    if (!deliveryOptions) return;
    assignDelivetyOptionLabels(deliveryOptions);
  }, [deliveryOptions]);

  const filteredDeliveryOptions = useMemo(
    () => filterDeliveryOptions(deliveryOptions, selectedMiddleMileFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedMiddleMileFilter, deliveryOptions]
  );

  useEffect(() => {
    if (isNextButtonClicked.value && !selectedDeliveryOption) {
      toastError(t('delivery_options_alert_select_option'));
      setIsNextButtonClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNextButtonClicked, selectedDeliveryOption]);

  const sortedDeliveryOption = useMemo(
    () => sortDeliveryOptions(filteredDeliveryOptions, activeSort.value),
    [
      filteredDeliveryOptions,
      activeSort.value,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      filteredDeliveryOptions?.at(0)?.labels,
      // We need labels here to re-run sorting when labels are assigned.
    ]
  );

  if (!filteredDeliveryOptions) return;

  return (
    <ServicesSubSectionContainerForDelivery>
      <MiddleMileFilter
        deliveryOptions={deliveryOptions}
        isAnimationTriggered={shouldTriggerShrinkWidthAnimation}
        onFilterSelected={(filterType) => {
          setMiddleMileFilter(
            selectedMiddleMileFilter === filterType ? undefined : filterType
          );
          setDeliveryOption(undefined);
        }}
        activeFilter={selectedMiddleMileFilter}
      />
      {!!selectedMiddleMileFilter && (
        <FlexContainer
          $mtMobile='.4rem'
          $align='center'
          $gap='1.2rem'>
          <ServicesSubSectionHeader>
            {t('delivery_option')}
          </ServicesSubSectionHeader>
          <DeliveryOptionsSorting
            activeSortOption={activeSort}
            onSortOptionSelected={setActiveSort}
          />
        </FlexContainer>
      )}
      <DeliveryOptionCardsContainer>
        {sortedDeliveryOption?.map((option) => (
          <DeliveryOptionCard
            key={option.uiKey}
            deliveryOption={option}
            currencySymbol={getCurrencySymbol(
              option.selectedFirstMile?.currency
            )}
            labels={option.labels}
            onCardSelected={(opt) => setDeliveryOption(opt)}
            onFirstMileSelected={setSelectedFirstMile}
            onLastMileSelected={setSelectedLastMile}
          />
        ))}
      </DeliveryOptionCardsContainer>
      <HintsSidebar hintsInfo={deliveryOptionHints} />
    </ServicesSubSectionContainerForDelivery>
  );
};

export default observer(ParcelDelivery);
